import React from 'react';
import { useSelector } from 'react-redux';
import * as R from 'ramda';
import { Box } from '@material-ui/core';

import { getPluginConfiguration } from 'reducers/Plugins';
import { PosPlugin } from 'plugins/plugin';

import { dictToArrayByOrder, generateAttributeName } from '../utils';
import {
  AnswerWithExtraInput,
  AnswerWithExtraInputDict,
  CustomerFormFeedbackConfig,
  Question,
} from '../types';

import FeedbackInputField from './FeedbackInputField';

const UiCustomerForm: PosPlugin['UICustomerForm'] = ({
  children,
  value,
  onChange,
}) => {
  const config = useSelector(
    getPluginConfiguration<CustomerFormFeedbackConfig>(
      'customer-creation-form-feedback-section',
    ),
  );

  const updateQAnswers = (
    qid: Question['id'],
    newAnswers: AnswerWithExtraInputDict,
  ) => {
    const question = config?.questions[qid];
    const attributeName =
      question.attributeName ?? generateAttributeName(question.id);

    const attributeValue = R.map(R.pick(['content', 'extraInput']), newAnswers);
    onChange(
      R.assocPath(
        ['attributes', attributeName],
        JSON.stringify(attributeValue),
        value,
      ),
    );
  };

  const extractAnswerToState = (
    qid: Question['id'],
  ): Record<AnswerWithExtraInput['id'], AnswerWithExtraInput> => {
    try {
      const question = config?.questions[qid];
      const attributeName =
        question.attributeName ?? generateAttributeName(question?.id);
      const attributeValue = JSON.parse(
        value?.attributes?.[attributeName] ?? '{}',
      );
      const answers = R.map(v => R.mergeDeepRight(question.answers[v.id], v))(
        attributeValue,
      );
      return (
        answers ??
        ({} as Record<AnswerWithExtraInput['id'], AnswerWithExtraInput>)
      );
    } catch (err) {
      console.error('Failed to load answers from for question', qid, err);
      return {} as Record<AnswerWithExtraInput['id'], AnswerWithExtraInput>;
    }
  };

  if (config?.onlyOnNewCustomer && value?.customerID) return children;

  return R.pipe(
    React.Children.toArray,
    R.concat(
      R.__,
      dictToArrayByOrder(config?.questions ?? {}).map(q => {
        return (
          <Box style={{ marginTop: '.5em' }}>
            <FeedbackInputField
              question={q}
              feedbackState={extractAnswerToState(q.id)}
              updateFeedback={updateQAnswers}
            />
          </Box>
        );
      }),
    ),
  )(children);
};

export default UiCustomerForm;
